import { useCallback, useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { axiosInstance } from '../../helpers'
import {
    ApartmentProspectsBrowseResponse,
    ApartmentProspectsSummaryResponse,
} from '../../models/Outreach/types'

export type ApartmentProspectsStatusSort = 'prospect_first' | 'target_first'

export interface ApartmentProspectsFilters {
    schoolId: number | null
    graduationYears: number[]
    search: string
    hasEmail: boolean
    hasPhone: boolean
    statusSort: ApartmentProspectsStatusSort
    page: number
    pageSize: number
}

const defaultFilters = (): ApartmentProspectsFilters => ({
    schoolId: null,
    graduationYears: [],
    search: '',
    hasEmail: false,
    hasPhone: false,
    statusSort: 'prospect_first',
    page: 1,
    pageSize: 25,
})

function graduationYearsParam(years: number[]): string | undefined {
    if (!years.length) return undefined
    return years.join(',')
}

export function useApartmentProspects(apartmentId: number | undefined) {
    const queryClient = useQueryClient()
    const [filters, setFilters] = useState<ApartmentProspectsFilters>(
        defaultFilters,
    )
    const [enrichingProspects, setEnrichingProspects] = useState<
        Array<{ id: number; field: string; taskId: string }>
    >([])

    const graduationParam = graduationYearsParam(filters.graduationYears)

    const summaryQuery = useQuery<ApartmentProspectsSummaryResponse>({
        queryKey: ['apartment-prospects-summary', apartmentId, graduationParam],
        queryFn: async () => {
            const res = await axiosInstance.get(
                '/outreach/apartment-prospects/summary/',
                {
                    params: graduationParam
                        ? { graduation_years: graduationParam }
                        : {},
                },
            )
            return res.data
        },
        enabled: apartmentId != null,
    })

    const browseQuery = useQuery<ApartmentProspectsBrowseResponse>({
        queryKey: [
            'apartment-prospects-browse',
            apartmentId,
            filters.schoolId,
            graduationParam,
            filters.search,
            filters.hasEmail,
            filters.hasPhone,
            filters.statusSort,
            filters.page,
            filters.pageSize,
        ],
        queryFn: async () => {
            const params: Record<string, string | number | boolean> = {
                page: filters.page,
                page_size: filters.pageSize,
                ordering:
                    filters.statusSort === 'prospect_first'
                        ? '-status'
                        : 'status',
            }
            if (filters.schoolId != null) {
                params.school_id = filters.schoolId
            }
            if (graduationParam) {
                params.graduation_years = graduationParam
            }
            if (filters.search) {
                params.search = filters.search
            }
            if (filters.hasEmail) params.has_email = true
            if (filters.hasPhone) params.has_phone = true

            const res = await axiosInstance.get(
                '/outreach/apartment-prospects/browse/',
                { params },
            )
            return res.data
        },
        enabled: apartmentId != null && filters.schoolId != null,
    })

    useEffect(() => {
        const schools = summaryQuery.data?.schools ?? []
        if (!schools.length) return
        if (
            filters.schoolId == null ||
            !schools.some((s) => s.school_id === filters.schoolId)
        ) {
            const primary = schools.find((s) => s.is_primary) ?? schools[0]
            setFilters((prev) => ({ ...prev, schoolId: primary.school_id }))
        }
    }, [summaryQuery.data, filters.schoolId])

    const setSchoolId = useCallback((schoolId: number) => {
        setFilters((prev) => ({
            ...prev,
            schoolId,
            graduationYears: [],
            search: '',
            hasEmail: false,
            hasPhone: false,
            statusSort: 'prospect_first',
            page: 1,
        }))
    }, [])

    const setGraduationYears = useCallback((years: number[]) => {
        setFilters((prev) => ({ ...prev, graduationYears: years, page: 1 }))
    }, [])

    const setSearch = useCallback((search: string) => {
        setFilters((prev) => ({ ...prev, search, page: 1 }))
    }, [])

    const setHasEmail = useCallback((hasEmail: boolean) => {
        setFilters((prev) => ({ ...prev, hasEmail, page: 1 }))
    }, [])

    const setHasPhone = useCallback((hasPhone: boolean) => {
        setFilters((prev) => ({ ...prev, hasPhone, page: 1 }))
    }, [])

    const setStatusSort = useCallback(
        (statusSort: ApartmentProspectsStatusSort) => {
            setFilters((prev) => ({ ...prev, statusSort, page: 1 }))
        },
        [],
    )

    const setPage = useCallback((page: number) => {
        setFilters((prev) => ({ ...prev, page }))
    }, [])

    const setPageSize = useCallback((pageSize: number) => {
        setFilters((prev) => ({ ...prev, pageSize, page: 1 }))
    }, [])

    const resetListFilters = useCallback(() => {
        setFilters((prev) => ({
            ...prev,
            graduationYears: [],
            search: '',
            hasEmail: false,
            hasPhone: false,
            statusSort: 'prospect_first',
            page: 1,
        }))
    }, [])

    const enrichProspectField = useMutation({
        mutationFn: ({
            prospectId,
            field,
        }: {
            prospectId: number
            field: 'email' | 'phone' | 'linkedinProfile'
        }) =>
            axiosInstance.post(`/outreach/prospects/${prospectId}/enrich/`, {
                field,
            }),
        onSuccess: (response, variables) => {
            setEnrichingProspects((prev) => [
                ...prev,
                {
                    id: variables.prospectId,
                    field: variables.field,
                    taskId: response.data.task_id,
                },
            ])
            toast.info('Enrichment started...')
        },
        onError: () => toast.error('Failed to start enrichment'),
    })

    const addProspectToCampaign = useMutation({
        mutationFn: ({
            prospectId,
            campaignId,
        }: {
            prospectId: number
            campaignId: number
        }) =>
            axiosInstance.post(
                `/outreach/prospects/${prospectId}/add-to-campaign/`,
                { campaign_id: campaignId },
            ),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: ['apartment-prospects-browse'],
            })
            void queryClient.invalidateQueries({
                queryKey: ['apartment-prospects-summary'],
            })
            toast.success('Added to campaign')
        },
        onError: () => toast.error('Failed to add to campaign'),
    })

    const uploadSpreadsheet = useMutation({
        mutationFn: async ({
            schoolId,
            file,
            headerRow,
            columnMapping,
            graduationYear,
        }: {
            schoolId: number
            file: File
            headerRow: number
            columnMapping: {
                name: number | null
                first_name_header: number | null
                last_name_header: number | null
                linkedinProfile: number | null
                email: number | null
                phone: number | null
            }
            graduationYear?: number | null
        }) => {
            const formData = new FormData()
            formData.append('file', file)
            formData.append('school_id', String(schoolId))
            formData.append('header_row', headerRow.toString())
            formData.append('column_mapping', JSON.stringify(columnMapping))
            if (graduationYear != null) {
                formData.append('graduation_year', String(graduationYear))
            }
            return axiosInstance.post(
                '/outreach/apartment-prospects/upload-spreadsheet/',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                },
            )
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: ['apartment-prospects-browse'],
            })
            void queryClient.invalidateQueries({
                queryKey: ['apartment-prospects-summary'],
            })
            toast.info('Upload started. This may take a few moments...')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to upload spreadsheet'
            toast.error(errorMessage)
        },
    })

    useEffect(() => {
        if (!enrichingProspects.length) return

        const poll = async () => {
            const remaining: typeof enrichingProspects = []
            for (const item of enrichingProspects) {
                try {
                    const res = await axiosInstance.get(
                        '/outreach/prospects/enrich_status/',
                        { params: { task_id: item.taskId } },
                    )
                    if (res.data.status !== 'completed') {
                        remaining.push(item)
                    }
                } catch {
                    remaining.push(item)
                }
            }
            if (remaining.length !== enrichingProspects.length) {
                void queryClient.invalidateQueries({
                    queryKey: ['apartment-prospects-browse'],
                })
                void queryClient.invalidateQueries({
                    queryKey: ['apartment-prospects-summary'],
                })
            }
            setEnrichingProspects(remaining)
        }

        const interval = setInterval(() => void poll(), 4000)
        return () => clearInterval(interval)
    }, [enrichingProspects, queryClient])

    return {
        summary: summaryQuery.data,
        isLoadingSummary: summaryQuery.isLoading,
        browse: browseQuery.data,
        isLoadingBrowse: browseQuery.isLoading,
        filters,
        setSchoolId,
        setGraduationYears,
        setSearch,
        setHasEmail,
        setHasPhone,
        setStatusSort,
        setPage,
        setPageSize,
        resetListFilters,
        enrichProspectField,
        enrichingProspects,
        addProspectToCampaign,
        uploadSpreadsheet,
        refetchBrowse: browseQuery.refetch,
    }
}
